import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import _ from "lodash";
import ConfirmDialog from "components/dialog/confirm";
import { AlertNotification } from "components/alert/alert-notification";
import { SeverityMessages } from "constants/product-collection";
import { DATATEST } from "constants/integration";
import { ICart } from "constants/cart";
import { IPromotion, IPromotionsData } from "constants/promotion-types";
import { FormatMessage } from "react-globalize";
import { format, formatCurrency } from "lib/globalize-util";
import { makeStyles } from "@material-ui/styles";
import { themeOptions } from "styles/theme";

import { CompanySelectionModal } from "components/cart/company-selection-modal";
import type { IScope } from "reducers/scope";
import { TFDMCompanyRecord } from "records/company/company";
import { IUser } from "containers/management/management-types";
import { IOrder } from "records/order";
import { DEFAULT_PROMOTION_COUNT_TO_SHOW } from "containers/admin/promotions/utils/consts";
interface IPromotionDetailsProps {
    id?: string;
    pricingPromotionEnabled: boolean;
    promotions: IPromotionsData;
    containerClassName?: string;
    totalApplied?: number;
}

interface IPromotionMessageProps {
    id: string;
    messages: string[];
    promotions: IPromotion[];
    displayModal: (promotion: IPromotion) => void;
    isApplied?: boolean;
}

interface IPromotionModalProps {
    selectedPromotion: IPromotion;
    onChange: (promotion: IPromotion) => void;
}

interface INoPromotionForCompanyProps {
    onCartFetch?: (params?: any) => void;
    onFetchStartOrderCompanies: (params) => void;
    onSnackbarAdd: (param: { message: string } | any) => void;
    onSyncFromCart: (order: IOrder, meta: { syncToCart?: boolean }) => void;
    fetchingCompanies: boolean;
    companies: TFDMCompanyRecord[];
    companyIds: string[];
    hasMoreCompanies: boolean;
    cart: ICart;
    user: IUser;
    scope: IScope;
    promotions: IPromotionsData;
}

export const PromotionMessage = ({
    promotions,
    messages,
    id,
    displayModal,
    isApplied = true,
}: IPromotionMessageProps) => {
    const [single, multi] = messages;
    const [allPointsVisible, setAllPointsVisible] = useState<boolean>(false);

    useEffect(() => {
        setAllPointsVisible(false);
    }, [id]);

    const classes = useStyles({});

    const handlePointsVisibility = useCallback(() => {
        setAllPointsVisible(!allPointsVisible);
    }, [allPointsVisible]);

    const handleDisplayModal = useCallback(() => {
        displayModal(promotions[0]);
    }, [displayModal, promotions]);

    const listOfPromos = useMemo(
        () => (allPointsVisible ? promotions : _.take(promotions, DEFAULT_PROMOTION_COUNT_TO_SHOW)),
        [allPointsVisible, promotions],
    );

    const displayPromotions = useMemo(() => {
        return _.map(listOfPromos, (promotion) => (
            <li key={promotion.name} className={classes.point}>
                <a className={classes.promotionLink} onClick={() => displayModal(promotion)}>
                    {promotion.name}
                </a>
                {promotion.eligibilityInfo && <span> - {promotion.eligibilityInfo}</span>}
            </li>
        ));
    }, [classes.point, classes.promotionLink, displayModal, listOfPromos]);

    return _.size(promotions) > 1 ? (
        <>
            <div className={classes.subtitle}>{multi}</div>
            <ul>{displayPromotions}</ul>
            {_.size(promotions) > DEFAULT_PROMOTION_COUNT_TO_SHOW && (
                <a className={classes.promotionLink} onClick={handlePointsVisibility}>
                    {allPointsVisible ? format("See less") : format("See all")}
                </a>
            )}
        </>
    ) : _.size(promotions) === 1 ? (
        <>
            <span className={classes.subtitle}>{single}</span>
            <a className={classes.promotionLink} onClick={handleDisplayModal}>
                {promotions[0].name}
            </a>
            {!isApplied && promotions[0].eligibilityInfo && <span> - {promotions[0].eligibilityInfo}</span>}
        </>
    ) : null;
};

export const PromotionModal = ({ selectedPromotion, onChange }: IPromotionModalProps) => {
    const hideModal = useCallback(() => {
        onChange(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const classes = useStyles({});

    return (
        <ConfirmDialog
            open={!!selectedPromotion}
            title={format("Promotion Description")}
            cancelLabel={format("Close")}
            onCancel={hideModal}
            hideConfirmButton={true}
            actionsContainerStyle={promotionModalStyles.actionsStyle}
            closeButtonStyle={promotionModalStyles.dialogCloseButtom}
            contentStyle={promotionModalStyles.confirmDialog}
            titleStyle={promotionModalStyles.titleStyle}
            bodyStyle={promotionModalStyles.bodyStyle}
            dataTestCancel={DATATEST.PRICING_SERVICE_PROMOTIONS.PROMOTION_MODAL.CANCEL}
        >
            {selectedPromotion && (
                <div className={classes.content} data-test={DATATEST.PRICING_SERVICE_PROMOTIONS.PROMOTION_MODAL.MODAL}>
                    <h4
                        className={classes.promotionName}
                        data-test={DATATEST.PRICING_SERVICE_PROMOTIONS.PROMOTION_MODAL.NAME}
                    >
                        {selectedPromotion.name}
                    </h4>
                    <div data-test={DATATEST.PRICING_SERVICE_PROMOTIONS.PROMOTION_MODAL.DESCRIPTION}>
                        {_.map(_.get(selectedPromotion, "description", "").split("\n"), (paragraph, index) => {
                            paragraph = paragraph.trim();
                            return paragraph ? (
                                <p key={`description-${index}`}>{paragraph}</p>
                            ) : (
                                <br key={`description-${index}`} />
                            );
                        })}
                    </div>
                </div>
            )}
        </ConfirmDialog>
    );
};

export const PromotionDetails = ({
    promotions,
    pricingPromotionEnabled,
    id,
    containerClassName,
    totalApplied,
}: IPromotionDetailsProps) => {
    const [showInfoBanner, setShowInfoBanner] = useState<boolean>(false);
    const [showSuccessBanner, setShowSuccessBanner] = useState<boolean>(false);
    const [selectedPromotion, setSelectedPromotion] = useState<IPromotion>(null);

    const closeInfoBanner = useCallback(() => setShowInfoBanner(false), []);
    const closeSuccessBanner = useCallback(() => setShowSuccessBanner(false), []);

    useEffect(() => {
        setShowInfoBanner(pricingPromotionEnabled);
        setShowSuccessBanner(pricingPromotionEnabled);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const classes = useStyles({});

    if (!promotions || (_.isEmpty(promotions.applied) && _.isEmpty(promotions.threshold))) {
        return null;
    }

    const thresholdTextList = [
        format("You are close to qualifying for a promotion!"),
        format("|amount| Eligible promotions:", { amount: _.size(promotions.threshold) }),
    ];
    const thresholdMessage = !_.isEmpty(promotions.threshold) && (
        <PromotionMessage
            promotions={promotions.threshold}
            messages={thresholdTextList}
            id={id}
            displayModal={setSelectedPromotion}
        />
    );

    const appliedTextList = [
        format("Promotion applied!"),
        format("|amount| Promotions applied:", { amount: _.size(promotions.applied) }),
    ];
    const appliedMessage = !_.isEmpty(promotions.applied) && (
        <PromotionMessage
            promotions={promotions.applied}
            messages={appliedTextList}
            isApplied={false}
            id={id}
            displayModal={setSelectedPromotion}
        />
    );

    return (
        <div>
            {appliedMessage && (
                <AlertNotification
                    className={classes.alert}
                    message={appliedMessage}
                    severity={SeverityMessages.Success}
                    open={showInfoBanner}
                    onClose={closeInfoBanner}
                    resultMessage={format("Total savings applied")}
                    resultValue={totalApplied > 0 ? formatCurrency(totalApplied, promotions.currencyCode) : null}
                    dataTest={DATATEST.APPLIED_PROMOTIONS_MESSAGE}
                />
            )}
            {thresholdMessage && (
                <AlertNotification
                    className={classes.alert}
                    message={thresholdMessage}
                    severity={SeverityMessages.Info}
                    open={showSuccessBanner}
                    onClose={closeSuccessBanner}
                />
            )}
            <PromotionModal selectedPromotion={selectedPromotion} onChange={setSelectedPromotion} />
        </div>
    );
};

export const NoPromotionForCompany: React.FC<INoPromotionForCompanyProps> = ({
    promotions,
    onCartFetch = null,
    cart,
    onFetchStartOrderCompanies,
    onSnackbarAdd,
    onSyncFromCart,
    fetchingCompanies,
    companies,
    companyIds,
    hasMoreCompanies,
    user,
    scope,
}) => {
    const [showInfoBanner, setShowInfoBanner] = useState<boolean>(true);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [companyUpdated, setCompanyUpdated] = useState<boolean>(false);
    const prevCompany = useRef<{ retailer_name: string; ref: string }>();

    const wo = useMemo(() => _.get(cart?.entities?.orders, cart?.result), [cart]);

    const closeInfoBanner = useCallback(() => setShowInfoBanner(false), []);

    const handleOpenModal = useCallback(() => setOpenModal(true), []);
    const handleCloseModal = useCallback(() => {
        setOpenModal(false);
    }, []);
    const handleSubmitModal = useCallback(() => {
        setOpenModal(false);
        onCartFetch?.();
        setCompanyUpdated(true);
    }, [onCartFetch]);

    useEffect(() => {
        if (companyUpdated && wo && wo.retailer?.ref !== prevCompany.current?.ref) {
            if (companyUpdated) {
                setCompanyUpdated(false);
                if (promotions && (_.size(promotions.applied) || _.size(promotions.threshold))) {
                    onSnackbarAdd({
                        message: format("|companyName| is eligible for promotions. Prices will be updated", {
                            companyName: wo.retailer?.retailer_name,
                        }),
                    });
                } else {
                    onSnackbarAdd({
                        message: format("No promotions found for |companyName|", {
                            companyName: wo.retailer?.retailer_name,
                        }),
                    });
                }
            }
            // company was changed
            prevCompany.current = wo.retailer;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyUpdated, wo?.retailer, prevCompany, promotions]);

    useEffect(() => {
        prevCompany.current = null;
    }, []);

    const classes = useStyles({});

    if (!scope.brand.isPricingPromotionEnabled(scope) || _.isEmpty(cart?.result) || !_.isEmpty(wo?.retailer)) {
        return null;
    }

    return (
        <div>
            <AlertNotification
                className={classes.alert}
                dataTest={DATATEST.GALLERY.PROMOTION_NO_COMPANY}
                message={
                    <FormatMessage
                        path="Select a company prior to building your order and see if there are any eligible company-level promotions."
                        elements={{
                            link: (
                                <span
                                    data-test={DATATEST.GALLERY.PROMOTION_NO_COMPANY_SELECT}
                                    className={classes.link}
                                    onClick={handleOpenModal}
                                />
                            ),
                        }}
                    />
                }
                severity={SeverityMessages.Info}
                open={showInfoBanner}
                onClose={closeInfoBanner}
            />
            <CompanySelectionModal
                open={openModal}
                onClose={handleCloseModal}
                onConfirm={handleSubmitModal}
                onFetchStartOrderCompanies={onFetchStartOrderCompanies}
                onSnackbarAdd={onSnackbarAdd}
                onSyncFromCart={onSyncFromCart}
                fetchingCompanies={fetchingCompanies}
                companies={companies}
                companyIds={companyIds}
                hasMoreCompanies={hasMoreCompanies}
                orderId={cart?.result}
                user={user}
                scope={scope}
            />
        </div>
    );
};

const useStyles = makeStyles({
    promotionLink: {
        color: "inherit",
        textDecoration: "underline",
        cursor: "pointer",
        "&:hover": {
            color: "inherit",
        },
    },
    alert: {
        width: "100% !important",
        margin: "0 0 5px !important",
    },
    content: {
        color: themeOptions.palette.textColor,
        fontSize: "14px",
    },
    subtitle: {
        fontWeight: "bold",
        marginRight: "5px",
    },
    promotionName: {
        fontWeight: "bold",
        margin: "18px 5px 11px 0",
    },
    point: {
        listStyleType: "disc",
    },
    link: {
        textDecoration: "underline",
        cursor: "pointer",
    },
});

const promotionModalStyles = {
    confirmDialog: {
        maxWidth: "600px",
    },
    dialogCloseButtom: {
        top: "12px",
        right: "14px",
    },
    titleStyle: {
        fontSize: "16px",
        fontWeight: "bold",
        marginLeft: "0px",
        marginRight: "0px",
        padding: "12px 24px",
        borderBottom: "1px solid lightgray",
    },
    actionsStyle: {
        padding: "40px 24px 24px",
    },
    bodyStyle: {
        borderBottom: "none",
        borderTop: "none",
    },
};
